import { useAppLocale } from '~/composables/custom/useAppLocale';
import customerGetters from '~/composables/getters/customerGetters';
import type { Customer } from '~/types/GraphQL';

// Guest → so long as he didn’t log in
// NewCustomer → when he registeres new in the profile or during the checkout
// Customer → when he logs in with a current account in the profile or during the checkout
// Employees → when he logs in with a current account in the profile or during the checkout and has the customer group for employees
const CUSTOMER_TYPES = {
  GUEST: 'Guest',
  NEW_CUSTOMER: 'NewCustomer',
  CUSTOMER: 'Customer',
  EMPLOYEES: 'Employees',
};

const calculateBreakpoint = () => {
  if (typeof window === 'undefined') {
    return 'DES'; // Default fallback for SSR/Test environment
  }
  const width = window.screen?.width || 1024;

  if (width < 768) {
    return 'MOB';
  }
  if (width < 1024) {
    return 'TAB';
  }
  return 'DES';
};

export interface TrackingProduct {
  item_id: string;
  currency: string;
  index: number;
  item_category1?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_color: string;
  item_group: string;
  item_ekb: string;
  item_name: string;
  item_product: string; // product key
  price: number;
  quantity?: number;
}

export const useGtm = () => {
  const { country, isoCode } = useAppLocale();
  const { $gtm } = useNuxtApp();

  const delayedGtmPageView = (user: Customer, isAuthenticated: boolean) => {
    // Skip delayed execution in test environment
    if (process.env.NODE_ENV === 'test') {
      gtmPageView(user, isAuthenticated);
      return;
    }

    setTimeout(() => {
      gtmPageView(user, isAuthenticated);
    }, 500);
  };

  const gtmTracking = <T extends { event: string }>(data: T) => {
    if (!$gtm || data.event === 'pageView') {
      return;
    }

    $gtm.push(data);
  };

  const gtmPageView = (user: Customer, isAuthenticated: boolean) => {
    // Skip if GTM is not available (e.g., in test environment)
    if (!$gtm) {
      return;
    }

    let customerType = CUSTOMER_TYPES.GUEST;
    if (customerGetters.isKikEmployee(user?.customerGroup?.key)) {
      customerType = CUSTOMER_TYPES.EMPLOYEES;
    }
    else if (user?.createdAt ? new Date(user?.createdAt).toLocaleDateString() === new Date().toLocaleDateString() : false) { // ???
      customerType = CUSTOMER_TYPES.NEW_CUSTOMER;
    }
    else if (isAuthenticated) {
      customerType = CUSTOMER_TYPES.CUSTOMER;
    }

    $gtm.push({
      event: 'pageView',
      breakpoint: calculateBreakpoint(),
      country: isoCode,
      customerType,
      language: country,
      pageType: 'ECOM',
      pageType2: 'B2C',
    });
  };

  const gtmProductView = (product: TrackingProduct) => {
    gtmTracking({
      event: 'view_item',
      ecommerce: {
        items: [product],
      },
    });
  };

  const gtmAddToCart = (product: TrackingProduct, variantSku: string) => {
    gtmTracking({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            ...product,
            // eslint-disable-next-line camelcase
            item_variant: variantSku,
            price: product.price * (product.quantity || 0),
          },
        ],
      },
    });
  };

  return {
    gtmTracking,
    gtmPageView,
    gtmProductView,
    gtmAddToCart,
    delayedGtmPageView,
  };
};
